import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { tryRedeemStoredInvite, describeInviteError } from "@/lib/inviteToken";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import { ensureProfile } from "@/lib/oauth-helpers";

const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes
const INACTIVITY_WARNING_MS = 60 * 1000; // show "stay signed in?" 60s before logout

interface AuthCtx {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  /** Seconds remaining until forced inactivity logout, or null when not warning. */
  inactivityWarningSeconds: number | null;
  /** Reset the inactivity timer (used by the "Stay signed in" button). */
  dismissInactivityWarning: () => void;
}

const AuthContext = createContext<AuthCtx>({
  session: null,
  user: null,
  loading: true,
  signOut: async () => {},
  inactivityWarningSeconds: null,
  dismissInactivityWarning: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [inactivityWarningSeconds, setInactivityWarningSeconds] = useState<number | null>(null);
  const redeemedRef = useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setLoading(false);
      if (!session) {
        redeemedRef.current = false;
        clearSentryUser();
      } else if (session.user) {
        setSentryUser({ id: session.user.id });
        // Self-heal: make sure every signed-in user has a profile + agency.
        // Cheap no-op if both already exist. Critical for OAuth users whose
        // first sign-in race-conditioned the handle_new_user trigger.
        void ensureProfile();
        // Stamp last_login on sign-in and on session restore so the team page shows accurate activity.
        // Uses a SECURITY DEFINER RPC because the direct profile UPDATE policy was removed.
        if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
          // .rpc() builders are lazy thenables — the request only fires once
          // consumed via .then()/await, so a bare `void` on the builder never
          // sends it.
          void (supabase as any).rpc("update_last_login").then(() => {});
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      if (session?.user) setSentryUser({ id: session.user.id });
    });

    return () => subscription.unsubscribe();
  }, []);

  // Auto-redeem any pending invite once we have a confirmed session.
  // Runs after `loading` flips false so it can't race the initial profile load,
  // and retries once on transient network failure.
  useEffect(() => {
    if (loading || !session || redeemedRef.current) return;
    redeemedRef.current = true;
    let cancelled = false;
    (async () => {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const result = await tryRedeemStoredInvite();
          if (cancelled) return;
          if (result?.ok) {
            toast.success("You've joined your team!");
          } else if (result?.error) {
            const message = describeInviteError(result.error);
            if (message) toast.warning(message);
          }
          return;
        } catch (err) {
          if (attempt === 1) {
            console.warn("[useAuth] invite redeem failed after retry:", err);
            return;
          }
          await new Promise(r => setTimeout(r, 1500));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [loading, session]);

  const signOut = useCallback(async () => {
    // Log logout before signing out
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      try {
        await (supabase as any).rpc("log_audit_event", { _action: "logout" });
      } catch {}
    }
    await supabase.auth.signOut();
  }, []);

  const clearAllTimers = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (warnTimerRef.current) { clearTimeout(warnTimerRef.current); warnTimerRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    setInactivityWarningSeconds(null);
  }, []);

  const dismissInactivityWarning = useCallback(() => {
    // User confirmed they're still here — wipe timers and the parent effect
    // will re-arm them on the next activity tick.
    clearAllTimers();
    // Synthesize an activity event so the listener-driven reset re-runs.
    window.dispatchEvent(new Event("mousedown"));
  }, [clearAllTimers]);

  // Inactivity timeout
  useEffect(() => {
    if (!session) return;

    function resetTimer() {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warnTimerRef.current) clearTimeout(warnTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      setInactivityWarningSeconds(null);

      // Schedule the warning dialog 60s before the hard logout fires.
      warnTimerRef.current = setTimeout(() => {
        let remaining = Math.floor(INACTIVITY_WARNING_MS / 1000);
        setInactivityWarningSeconds(remaining);
        countdownRef.current = setInterval(() => {
          remaining -= 1;
          if (remaining <= 0) {
            if (countdownRef.current) clearInterval(countdownRef.current);
          } else {
            setInactivityWarningSeconds(remaining);
          }
        }, 1000);
      }, INACTIVITY_MS - INACTIVITY_WARNING_MS);

      timerRef.current = setTimeout(() => {
        toast.info("Session expired due to inactivity");
        signOut();
      }, INACTIVITY_MS);
    }

    // Note: `mousemove` is intentionally excluded — a stationary cursor on
    // the page should not count as activity, otherwise the HIPAA 30-min
    // inactivity timeout would never fire for an idle user.
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearAllTimers();
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [session, signOut, clearAllTimers]);

  return (
    <AuthContext.Provider value={{
      session,
      user: session?.user ?? null,
      loading,
      signOut,
      inactivityWarningSeconds,
      dismissInactivityWarning,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
